import * as React from "react";
import { CircleCheck, Info, OctagonX, TriangleAlert } from "lucide-react";

export type BrokerAlertTone = "success" | "warning" | "error" | "info";

/** Vertical gap between AlertTitle and body (~one line of text-sm). */
export const BROKER_ALERT_CLASS =
  "!gap-y-5 rounded-lg font-[family-name:var(--font-primary)] normal-case tracking-normal";

export const BROKER_ALERT_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-bold normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

/**
 * Body copy under the alert title.
 * `!` beats Alert info variant `*:data-[slot=alert-description]:text-muted-foreground`.
 * Dark body matches IBKR guidance (#94A3B8), not pure white.
 */
export const BROKER_ALERT_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 normal-case tracking-normal !text-gray-500 dark:!text-slate-400 [&_*]:!font-[family-name:var(--font-primary)]";

/** Field descriptions on broker / account-settings detail cards. */
export const BROKER_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 normal-case tracking-normal !text-gray-500 dark:!text-white";

export const BROKER_ALERT_HEADING_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-bold normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

export const BROKER_ALERT_NOTE_CLASS =
  "font-[family-name:var(--font-primary)] text-xs italic leading-[1.55] normal-case tracking-normal !text-gray-500 dark:!text-slate-400";

export const BROKER_ALERT_EMPHASIS_CLASS =
  "font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

/** Read-only outline badge — Tropical Surf stroke + label (no fill). */
export const BROKER_OUTLINE_BADGE_CLASS =
  "inline-flex h-5 shrink-0 items-center justify-center rounded-[6px] border border-solid border-[color:var(--brand-tropical-surf)] bg-transparent px-2 py-0 font-[family-name:var(--font-primary)] text-xs font-normal leading-none normal-case tracking-normal text-[color:var(--brand-tropical-surf)] shadow-none";

/** Read-only pill badges — matches broker TagsInput pills and credential tag chips. */
export const BROKER_PILL_BADGE_CLASS =
  "inline-flex h-6 shrink-0 items-center rounded-[4px] border border-[color-mix(in_oklab,var(--brand-tropical-surf)_45%,transparent)] bg-[color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)] px-2 font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

export const BROKER_PILL_BADGE_LABEL_STYLE: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  lineHeight: 1,
  margin: 0,
  padding: "2px 0 0",
};

export function brokerAlertVariant(
  tone: BrokerAlertTone
): "success" | "warning" | "error" | "destructive" | "info" {
  switch (tone) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "error";
    default:
      return "info";
  }
}

export function BrokerAlertIcon({
  tone,
}: {
  tone: BrokerAlertTone;
}): React.ReactElement {
  switch (tone) {
    case "success":
      return <CircleCheck aria-hidden />;
    case "warning":
      return <TriangleAlert aria-hidden />;
    case "error":
      return <OctagonX aria-hidden />;
    default:
      return <Info aria-hidden />;
  }
}
